import ContentCopyRounded from '@mui/icons-material/ContentCopyRounded';
import {
    Alert,
    Box,
    Button,
    Checkbox,
    CircularProgress,
    FormControlLabel,
    Grid,
    IconButton,
    Stack,
    Step,
    StepLabel,
    Stepper,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material';
import { FormEvent, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSnackbar } from 'notistack';
import { betterAuthClient } from '@/libs/better-auth-client';
import { authApi } from '@/pages/public/auth/auth-api';
import { useHasPassword } from '@/hooks/use-has-password';

// QR code rendering via canvas — avoids a heavy dependency
async function uriToQrDataUrl(uri: string): Promise<string> {
    const { default: QRCode } = await import('qrcode');
    return QRCode.toDataURL(uri, { width: 200, margin: 1 });
}

type Phase = 'password' | 'scan' | 'recovery';

export default function TwoFactorEnrollSetup({
    onComplete,
    prefillPassword,
    isLegacyReenrollment = false,
}: {
    onComplete: () => void;
    prefillPassword?: string;
    isLegacyReenrollment?: boolean;
}) {
    const { t } = useTranslation();
    const { enqueueSnackbar } = useSnackbar();
    // Social-only accounts have no password; with server-side allowPasswordless they enable 2FA gated
    // by the step-up security-session instead of a password, so we skip the password phase for them.
    const { hasPassword } = useHasPassword();
    const isPasswordless = hasPassword === false;

    // If prefillPassword is provided we skip the password phase entirely
    const [phase, setPhase] = useState<Phase>(prefillPassword ? 'scan' : 'password');
    const [password, setPassword] = useState(prefillPassword ?? '');
    const [passwordLoading, setPasswordLoading] = useState(false);

    const [manualKey, setManualKey] = useState<string | null>(null);
    const [qrDataUrl, setQrDataUrl] = useState<string | null>(null);

    const [code, setCode] = useState('');
    const [verifyLoading, setVerifyLoading] = useState(false);
    const [codeError, setCodeError] = useState('');

    // Recovery phase state
    const [backupCodes, setBackupCodes] = useState<string[]>([]);
    const [savedConfirmed, setSavedConfirmed] = useState(false);
    const [completeLoading, setCompleteLoading] = useState(false);
    const [copiedIndex, setCopiedIndex] = useState<number | null>(null);
    const [copiedAll, setCopiedAll] = useState(false);

    // When prefillPassword is provided, call twoFactor.enable() on mount automatically
    useEffect(() => {
        if (!prefillPassword) return;

        let cancelled = false;
        setPasswordLoading(true);

        betterAuthClient.twoFactor
            .enable({ password: prefillPassword })
            .then((result) => {
                if (cancelled) return;
                if (result.error) {
                    enqueueSnackbar(
                        typeof result.error.message === 'string'
                            ? result.error.message
                            : t('auth.two-factor-setup.enroll.error.default'),
                    );
                    setPhase('password');
                    return;
                }

                const uri = (result.data as { totpURI: string }).totpURI;
                const secretMatch = uri.match(/[?&]secret=([^&]+)/i);
                setManualKey(secretMatch ? secretMatch[1] : null);

                uriToQrDataUrl(uri)
                    .then((dataUrl) => {
                        if (!cancelled) setQrDataUrl(dataUrl);
                    })
                    .catch(() => {
                        // QR render failed — manual key still available
                    });
            })
            .catch(() => {
                if (!cancelled) {
                    enqueueSnackbar(t('auth.two-factor-setup.enroll.error.default'));
                    setPhase('password');
                }
            })
            .finally(() => {
                if (!cancelled) setPasswordLoading(false);
            });

        return () => {
            cancelled = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Passwordless (social-only) accounts: auto-enable without a password once we know there's no
    // credential (allowPasswordless makes the password optional; the empty string is ignored server-side).
    useEffect(() => {
        if (prefillPassword || !isPasswordless || phase !== 'password') {
            return;
        }

        let cancelled = false;
        setPasswordLoading(true);

        betterAuthClient.twoFactor
            .enable({ password: '' })
            .then((result) => {
                if (cancelled) return;
                if (result.error) {
                    enqueueSnackbar(
                        typeof result.error.message === 'string'
                            ? result.error.message
                            : t('auth.two-factor-setup.enroll.error.default'),
                    );
                    return;
                }

                const uri = (result.data as { totpURI: string }).totpURI;
                const secretMatch = uri.match(/[?&]secret=([^&]+)/i);
                setManualKey(secretMatch ? secretMatch[1] : null);

                uriToQrDataUrl(uri)
                    .then((dataUrl) => {
                        if (!cancelled) setQrDataUrl(dataUrl);
                    })
                    .catch(() => {
                        // QR render failed — manual key still available
                    });

                setPhase('scan');
            })
            .catch(() => {
                if (!cancelled) enqueueSnackbar(t('auth.two-factor-setup.enroll.error.default'));
            })
            .finally(() => {
                if (!cancelled) setPasswordLoading(false);
            });

        return () => {
            cancelled = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isPasswordless]);

    const handlePasswordSubmit = async (event: FormEvent) => {
        event.preventDefault();
        if (!password) return;

        setPasswordLoading(true);
        try {
            const result = await betterAuthClient.twoFactor.enable({ password });
            if (result.error) {
                enqueueSnackbar(
                    typeof result.error.message === 'string'
                        ? result.error.message
                        : t('auth.two-factor-setup.enroll.error.default'),
                );
                return;
            }

            const uri = (result.data as { totpURI: string }).totpURI;

            const secretMatch = uri.match(/[?&]secret=([^&]+)/i);
            setManualKey(secretMatch ? secretMatch[1] : null);

            try {
                const dataUrl = await uriToQrDataUrl(uri);
                setQrDataUrl(dataUrl);
            } catch {
                // QR render failed — manual key still available
            }

            setPhase('scan');
        } catch {
            enqueueSnackbar(t('auth.two-factor-setup.enroll.error.default'));
        } finally {
            setPasswordLoading(false);
        }
    };

    const handleCodeSubmit = async (event: FormEvent) => {
        event.preventDefault();

        const normalizedCode = code.replace(/\D/g, '').slice(0, 6);
        if (normalizedCode.length !== 6) {
            setCodeError(t('auth.two-factor-setup.enroll.error.default'));
            return;
        }

        setCodeError('');
        setVerifyLoading(true);
        try {
            const result = await betterAuthClient.twoFactor.verifyTotp({ code: normalizedCode });
            if (result.error) {
                setCodeError(
                    typeof result.error.message === 'string'
                        ? result.error.message
                        : t('auth.two-factor-setup.enroll.error.default'),
                );
                return;
            }

            // TOTP confirmed — fetch backup codes before completing
            const codesResult = await betterAuthClient.twoFactor.generateBackupCodes({
                password: password || undefined,
            });
            if (codesResult.error || !codesResult.data?.backupCodes?.length) {
                enqueueSnackbar(t('auth.two-factor-setup.recovery.error.load'));
                return;
            }

            setBackupCodes(codesResult.data.backupCodes);
            setPhase('recovery');
        } catch {
            enqueueSnackbar(t('auth.two-factor-setup.enroll.error.default'));
        } finally {
            setVerifyLoading(false);
        }
    };

    const handleCopyCode = (codeStr: string, index: number) => {
        navigator.clipboard.writeText(codeStr).then(() => {
            setCopiedIndex(index);
            setTimeout(() => setCopiedIndex(null), 1500);
        });
    };

    const handleCopyAll = () => {
        navigator.clipboard.writeText(backupCodes.join('\n')).then(() => {
            setCopiedAll(true);
            setTimeout(() => setCopiedAll(false), 1500);
        });
    };

    const handleComplete = async () => {
        setCompleteLoading(true);
        try {
            // Only the legacy re-enrollment flow has a staged flag to clear; a fresh enrollment (e.g.
            // from settings) has none, and calling the endpoint would 400 with "re-enrollment is not
            // pending". The authenticator is already enabled via verifyTotp either way.
            if (isLegacyReenrollment) {
                await authApi.completeTwoFactorReenrollment();
            }
            onComplete();
        } catch {
            enqueueSnackbar(t('auth.two-factor-setup.enroll.error.default'));
        } finally {
            setCompleteLoading(false);
        }
    };

    // ─── Password phase ────────────────────────────────────────────────────────

    const STEPS: Phase[] = ['password', 'scan', 'recovery'];
    const activeStep = STEPS.indexOf(phase);

    const stepLabels: Record<Phase, string> = {
        password: t('auth.two-factor-setup.enroll.step.password'),
        scan: t('auth.two-factor-setup.enroll.step.scan'),
        recovery: t('auth.two-factor-setup.enroll.step.recovery'),
    };

    // While detecting the account type, or auto-enabling a passwordless account, show a spinner instead
    // of the password form (a social-only account should never see a "password" field).
    if (phase === 'password' && (isPasswordless || hasPassword === undefined)) {
        return (
            <Stack alignItems="center" py={6}>
                <CircularProgress />
            </Stack>
        );
    }

    if (phase === 'password') {
        return (
            <Stack spacing={3}>
                <Stepper activeStep={activeStep} alternativeLabel>
                    {STEPS.map((s) => (
                        <Step key={s}>
                            <StepLabel>{stepLabels[s]}</StepLabel>
                        </Step>
                    ))}
                </Stepper>

                {/* Live region announces phase transitions to screen readers */}
                <Box
                    component="span"
                    role="status"
                    aria-live="polite"
                    sx={{
                        position: 'absolute',
                        width: 1,
                        height: 1,
                        overflow: 'hidden',
                        clip: 'rect(0,0,0,0)',
                        whiteSpace: 'nowrap',
                    }}
                >
                    {stepLabels[phase]}
                </Box>

                <Stack spacing={1}>
                    <Typography variant="h5" fontWeight={700}>
                        {t('auth.two-factor-setup.enroll.title')}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        {t('auth.two-factor-setup.enroll.subtitle')}
                    </Typography>
                </Stack>

                <Stack component="form" spacing={2} onSubmit={handlePasswordSubmit}>
                    <TextField
                        label={t('str.password')}
                        type="password"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        required
                        autoFocus
                        autoComplete="current-password"
                        fullWidth
                    />

                    <Button
                        type="submit"
                        variant="contained"
                        size="large"
                        fullWidth
                        loading={passwordLoading}
                    >
                        {t('auth.two-factor-setup.enroll.continue')}
                    </Button>
                </Stack>
            </Stack>
        );
    }

    // ─── Scan / verify phase ───────────────────────────────────────────────────

    if (phase === 'scan') {
        return (
            <Stack spacing={3}>
                <Stepper activeStep={activeStep} alternativeLabel>
                    {STEPS.map((s) => (
                        <Step key={s}>
                            <StepLabel>{stepLabels[s]}</StepLabel>
                        </Step>
                    ))}
                </Stepper>

                {/* Live region announces phase transitions to screen readers */}
                <Box
                    component="span"
                    role="status"
                    aria-live="polite"
                    sx={{
                        position: 'absolute',
                        width: 1,
                        height: 1,
                        overflow: 'hidden',
                        clip: 'rect(0,0,0,0)',
                        whiteSpace: 'nowrap',
                    }}
                >
                    {stepLabels[phase]}
                </Box>
                <Stack spacing={1}>
                    <Typography variant="h5" fontWeight={700}>
                        {t('auth.two-factor-setup.enroll.title')}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        {t('auth.two-factor-setup.enroll.scan-label')}
                    </Typography>
                </Stack>

                <Alert severity="info">{t('auth.two-factor-setup.confirm.migration-notice')}</Alert>

                {passwordLoading && (
                    <Stack alignItems="center" py={4}>
                        <CircularProgress />
                    </Stack>
                )}

                {!passwordLoading && qrDataUrl && (
                    <Stack alignItems="center">
                        <Box
                            component="img"
                            src={qrDataUrl}
                            alt="TOTP QR code"
                            sx={{ width: 200, height: 200, borderRadius: 2 }}
                        />
                    </Stack>
                )}

                {!passwordLoading && manualKey && (
                    <Stack spacing={0.5}>
                        <Typography variant="caption" color="text.secondary">
                            {t('auth.two-factor-setup.enroll.manual-key-label')}
                        </Typography>
                        <Typography
                            variant="body2"
                            fontFamily="monospace"
                            sx={{
                                bgcolor: 'action.hover',
                                px: 1.5,
                                py: 1,
                                borderRadius: 1,
                                wordBreak: 'break-all',
                                userSelect: 'all',
                            }}
                        >
                            {manualKey}
                        </Typography>
                    </Stack>
                )}

                {!passwordLoading && (
                    <Stack component="form" spacing={2} onSubmit={handleCodeSubmit}>
                        <TextField
                            label={t('auth.two-factor-setup.enroll.code-label')}
                            value={code}
                            onChange={(e) => {
                                setCode(e.target.value.replace(/\D/g, '').slice(0, 6));
                                if (codeError) setCodeError('');
                            }}
                            required
                            autoFocus
                            autoComplete="one-time-code"
                            fullWidth
                            error={!!codeError}
                            helperText={codeError || t('auth.two-factor-setup.enroll.confirm-label')}
                            inputProps={{
                                inputMode: 'numeric',
                                pattern: '[0-9]*',
                                maxLength: 6,
                            }}
                        />

                        <Button
                            type="submit"
                            variant="contained"
                            size="large"
                            fullWidth
                            loading={verifyLoading}
                        >
                            {t('auth.two-factor-setup.enroll.submit')}
                        </Button>
                    </Stack>
                )}
            </Stack>
        );
    }

    // ─── Recovery codes phase ──────────────────────────────────────────────────

    return (
        <Stack spacing={3}>
            <Stepper activeStep={activeStep} alternativeLabel>
                {STEPS.map((s) => (
                    <Step key={s}>
                        <StepLabel>{stepLabels[s]}</StepLabel>
                    </Step>
                ))}
            </Stepper>

            {/* Live region announces phase transitions to screen readers */}
            <Box
                component="span"
                role="status"
                aria-live="polite"
                sx={{
                    position: 'absolute',
                    width: 1,
                    height: 1,
                    overflow: 'hidden',
                    clip: 'rect(0,0,0,0)',
                    whiteSpace: 'nowrap',
                }}
            >
                {stepLabels[phase]}
            </Box>
            <Stack spacing={1}>
                <Typography variant="h5" fontWeight={700}>
                    {t('auth.two-factor-setup.recovery.title')}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                    {t('auth.two-factor-setup.recovery.subtitle')}
                </Typography>
            </Stack>

            <Grid container spacing={1}>
                {backupCodes.map((c, i) => (
                    <Grid key={c} size={6}>
                        <Stack
                            direction="row"
                            alignItems="center"
                            justifyContent="space-between"
                            sx={{
                                bgcolor: 'action.hover',
                                px: 1.5,
                                py: 0.75,
                                borderRadius: 1,
                                height: '100%',
                            }}
                        >
                            <Typography variant="body2" fontFamily="monospace" letterSpacing={1}>
                                {c}
                            </Typography>
                            <Tooltip
                                title={
                                    copiedIndex === i
                                        ? t('auth.two-factor-setup.recovery.copied')
                                        : t('auth.two-factor-setup.recovery.copy-code')
                                }
                            >
                                <IconButton
                                    size="small"
                                    onClick={() => handleCopyCode(c, i)}
                                    aria-label={t('auth.two-factor-setup.recovery.copy-code')}
                                >
                                    <ContentCopyRounded fontSize="small" sx={{ color: 'text.primary' }} />
                                </IconButton>
                            </Tooltip>
                        </Stack>
                    </Grid>
                ))}
            </Grid>

            <Button variant="outlined" size="small" onClick={handleCopyAll} sx={{ alignSelf: 'flex-start' }}>
                {copiedAll
                    ? t('auth.two-factor-setup.recovery.copied')
                    : t('auth.two-factor-setup.recovery.copy-all')}
            </Button>

            <FormControlLabel
                control={
                    <Checkbox
                        checked={savedConfirmed}
                        onChange={(e) => setSavedConfirmed(e.target.checked)}
                    />
                }
                label={t('auth.two-factor-setup.recovery.saved-checkbox')}
            />

            <Button
                variant="contained"
                size="large"
                fullWidth
                disabled={!savedConfirmed}
                loading={completeLoading}
                onClick={handleComplete}
            >
                {t('auth.two-factor-setup.recovery.submit')}
            </Button>
        </Stack>
    );
}
